import {Component, OnInit, ViewChild, AfterViewInit} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {
  AlertService,
  CRMService,
  DeploymentLinkService,
  LoaderService,
  UserService,
  CampaignService,
} from '../_services';
import {FormBuilder, Validators} from '@angular/forms';
import {CrudSaveComponent} from '../_directives';
import {
  CRM,
  CRMType,
  CRMTypes,
  PluginType,
  CRMTypeId,
  LinkFieldType,
  User,
  CRMField,
  CRMFieldSet,
  Pager,
  DeploymentLink,
  SessionTypeLabels,
  Campaign,
  CustomDomain,
  getLinkTargetPath,
} from '../_models';
import {CRMTrackingComponent} from "./crm-tracking.component";
import {takeUntil} from "rxjs/operators";
import {CrmChargebackConfigComponent} from './crm-chargeback-config.component';
import {config} from "../../config/config";
import {NgxSmartModalService} from 'ngx-smart-modal';
import * as moment from 'moment';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './crm-edit.component.html',
  styleUrls: ['./crm.component.css'],
})
export class CRMNewComponent extends CrudSaveComponent implements OnInit, AfterViewInit {
  types: CRMType[] = [];
  subscriptionPlugins: PluginType[] = [];
  crm: CRM;
  selectedType: CRMType;
  selectedSubscriptionPlugin: PluginType;
  viewPrivateKey = false;
  user: User;
  fields: (CRMField | CRMFieldSet)[] = [];
  encryptedFields: (CRMField | CRMFieldSet)[] = [];
  deploymentLink: DeploymentLink;
  deploymentLinkColumns = ['Status', 'Type', 'Target Page', 'Domain', 'Last Modified'];
  campaign: Campaign;

  @ViewChild(CRMTrackingComponent, { static: false }) trackingFields: CRMTrackingComponent;
  @ViewChild(CrmChargebackConfigComponent, { static: false }) chargebackConfig: CrmChargebackConfigComponent;

  linkFieldTypes = [
    { value: LinkFieldType.UniqueId, label: 'Unique ID' },
    { value: LinkFieldType.ParentOrderId, label: 'Parent Order ID' },
    { value: LinkFieldType.PreviousOrderId, label: 'Previous Order ID' },
  ];

  parentCRMs: CRM[] = [];
  parentCRMSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    allowSearchFilter: false,
    enableCheckAll: false,
  };

  protected typeMap: {} = {};
  protected subscriptionPluginMap: {} = {};
  protected crms: CRM[] = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected crmService: CRMService,
    protected formBuilder: FormBuilder,
    protected alertService: AlertService,
    protected userService: UserService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected linkService: DeploymentLinkService,
    protected campaignService: CampaignService
  ) {
    super(router, location, route, crmService, alertService);
    this.isNew = true;
    this.objectName = 'CRM';
    this.title = 'Add New CRM';
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      type: [null, Validators.required],
      subscription_plugin: [null],
      max_fulfillments: [null, [Validators.min(0)]],
      max_transactions: [null, [Validators.min(0)]],
      auto_approve: [true],
      use_partial_refunds: [true],
      use_partial_charges: [true],
      allow_update_fulfillment: [true],
      link_id_field: [null],
      link_field_type: [LinkFieldType.UniqueId],
      credentials: this.formBuilder.group({}),
      config: this.formBuilder.group({}),
      display_name: [null],
      auto_sync: [true],
      parent_crms: [[]],
      store_credit_crm: [null],
      sync_using_batch: [false],
      owns_fulfillment: [false],
    });

    super.ngOnInit();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.crmService.getTypes().subscribe(
      (types: CRMTypes) => {
        this.types = types.crms;

        types.crms.forEach((type: CRMType) => {
          this.typeMap[type.id] = type;
        });

        types.subscription_plugins.forEach((plugin: PluginType) => {
          this.subscriptionPluginMap[plugin.id] = plugin;
        });

        if (this.crm) {
          this.selectedType = this.typeMap[this.crm.type];

          if (this.crm.subscription_plugin) {
            this.selectedSubscriptionPlugin = this.subscriptionPluginMap[this.crm.subscription_plugin];
          }

          if (!this.crm.display_name) {
            this.form.patchValue({ display_name: this.selectedType.name });
          }
        }

        this.updateFields();
      },
      (error) => {
        this.handleError(error);
      }
    );

    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });

    this.crmService
      .list({
        page: 1,
        page_size: config.maxPageSize,
        'type!': CRMTypeId.Test,
        is_deleted: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: Pager) => {
          if (data) {
            this.crms = data.results;
            this.onCrmsLoaded();
          }
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  protected onCrmsLoaded() {
    this.refreshParentCRMs();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.trackingFields) {
        this.form.addControl('tracking_data', this.trackingFields.form);
      }
    }, 0);
  }

  sync() {
    if (this.crm) {
      this.loading = true;

      this.crmService
        .sync(this.crm.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.loading = false;
          },
          (error) => {
            this.handleSubmitError(error);
            this.loading = false;
          }
        );
    }
  }

  get allowedTypes() {
    let types = this.types.filter((type: CRMType) => (this.crm && this.crm.type === type.id) || !type.auto_config);
    types.sort((a: CRMType, b: CRMType) => a.name.localeCompare(b.name));
    return types;
  }

  protected refreshParentCRMs() {
    // materialize into a stable array reference — ng-multiselect-dropdown breaks selection
    // if [data] returns a fresh array on every change-detection cycle (as a getter would)
    this.parentCRMs =
      this.selectedType && this.selectedType.id !== CRMTypeId.Test
        ? this.crms
            .filter((crm: CRM) => !this.crm || this.crm.id !== crm.id)
            .sort((a: CRM, b: CRM) => a.name.localeCompare(b.name))
        : [];
  }

  protected get crmNameMap(): { [key: string]: string } {
    return this.crms.reduce((map, crm: CRM) => {
      map[crm.id] = crm.name;
      return map;
    }, {});
  }

  selectType(event) {
    const selectedTypeId = event.target.value;

    if (selectedTypeId) {
      this.selectedType = this.typeMap[selectedTypeId];
    } else {
      this.selectedType = null;
    }

    if (this.selectedType) {
      this.form.patchValue({ display_name: this.selectedType.name });
    }

    this.form.patchValue({ subscription_plugin: null }); // clear this when type changes
    this.buildSubscriptionPluginList();
    this.selectedSubscriptionPlugin = null;
    this.updateFields();
  }

  protected buildSubscriptionPluginList() {
    let subscriptionPlugins = [];

    if (this.selectedType && this.selectedType.subscription_plugins) {
      this.selectedType.subscription_plugins.forEach((pluginId: number) => {
        subscriptionPlugins.push(this.subscriptionPluginMap[pluginId]);
      });
    }

    this.subscriptionPlugins = subscriptionPlugins;
  }

  selectSubscriptionPlugin(event) {
    const selectedId = event.target.value;

    if (selectedId) {
      this.selectedSubscriptionPlugin = this.subscriptionPluginMap[selectedId];
    } else {
      this.selectedSubscriptionPlugin = null;
    }

    this.updateFields();
  }

  get isCustomType() {
    return (
      this.selectedType &&
      (this.selectedType.id === CRMTypeId.Custom || this.selectedType.id === CRMTypeId.CustomExternalRefund)
    );
  }

  get isCustomExternalRefund() {
    return this.selectedType && this.selectedType.id === CRMTypeId.CustomExternalRefund;
  }

  get canShowControls() {
    return !this.isCustomExternalRefund;
  }

  getLinkTargetPath() {
    return getLinkTargetPath(this.deploymentLink);
  }

  getLinkTrackingSource() {
    return (
      this.deploymentLink.tracking_source &&
      this.deploymentLink.tracking_source['source'] &&
      this.deploymentLink.tracking_source['source'].split('-').join(' ')
    );
  }

  getLinkDomain() {
    const domain: CustomDomain = this.deploymentLink.domain as CustomDomain;
    return domain ? domain.domain : this.campaign.active_domain;
  }

  getLinkType() {
    return SessionTypeLabels[this.deploymentLink.link_type];
  }

  onSaveLink(deploymentLink: DeploymentLink) {
    this.deploymentLink = deploymentLink;
    this.modalService.getModal('shopifyDeploymentLinkDialog').close();
  }

  editLink(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.modalService.getModal('shopifyDeploymentLinkDialog').open();
  }

  deleteLink() {
    if (this.deploymentLink && window.confirm('Are you sure you want to delete this deployment link?')) {
      this.loaderService.show();
      this.linkService.delete(this.deploymentLink.id).subscribe(
        () => {
          this.alertService.success('Deleted deployment link.');
          this.loaderService.hide();
          this.deploymentLink = null;
        },
        (error) => {
          this.loaderService.hide();
          this.handleError(error);
        }
      );
    }
  }

  protected updateFields() {
    let fields = [];
    let encryptedFields = [];

    if (this.selectedType) {
      fields = this.selectedType.fields.slice();
      encryptedFields = this.selectedType.encrypted_fields.slice();

      if (this.selectedSubscriptionPlugin) {
        fields.push({
          fieldset: 'subscription_plugin',
          label: this.selectedSubscriptionPlugin.name,
          fields: this.selectedSubscriptionPlugin.fields,
        });
        encryptedFields.push({
          fieldset: 'subscription_plugin',
          label: this.selectedSubscriptionPlugin.name,
          fields: this.selectedSubscriptionPlugin.encrypted_fields,
        });
      }
    }

    this.fields = fields;
    this.encryptedFields = encryptedFields;
    this.refreshParentCRMs();
  }

  protected getFormData() {
    ['max_transactions', 'max_fulfillments'].forEach((field) => {
      if (field in this.form.value && this.form.value[field] && this.form.value[field].toString().trim() === '') {
        this.form.value[field] = null;
      }
    });

    let data = this.form.value;

    data = {
      ...data,
      parent_crms: (this.form.value.parent_crms || []).map((crm) => crm.id),
    };

    if (this.chargebackConfig) {
      data = { ...data, ...this.chargebackConfig.getFormData() };
    }

    return data;
  }

  getDeploymentDate(dateStr: string): string {
    const date = moment(dateStr).tz('EST');
    return date.format('DD MMM, YYYY');
  }

  protected onSaveComplete(data) {
    if (this.isCustomType) {
      this.loading = false;
      this.goBack();
    } else {
      this.crmService
        .sync(this.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (data) => {
            this.loading = false;
            this.goBack();
          },
          (error) => {
            this.loading = false;
            this.handleSubmitError(error);
          }
        );
    }
  }
}
